import { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BsCalendarCheck } from "react-icons/bs";
import ActionMenu from "../../../../component/ActionMenu";
import {
  getStatusBadge,
  getStatusText,
} from "../../../../component/admin/VehiclesTable";
import AdminPagination from "../../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../../component/SkeletonLoading";
import { useGetCancelledReservationsQuery } from "../../../../store/api/school/ReservationSlice";
import {
  convertTime,
  formatDateToLocal,
} from "../../../../utils/dateAndTimeConversion";
import "../../../../component/Model/reservation/ReservationDetails.scss";

const formatText = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const TRIP_LEG_LABEL = { outgoing: "Outgoing", return: "Return" };

const formatDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
};

const CancelledReservationsList = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [bookingTypeFilter, setBookingTypeFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);

  const { data, isLoading, isFetching } = useGetCancelledReservationsQuery({
    page,
    limit,
    booking_type: bookingTypeFilter || undefined,
    role_filter: role === "super_admin" ? roleFilter || undefined : undefined,
  });

  const handleBookingTypeFilter = (nextType) => {
    setBookingTypeFilter(nextType);
    setPage(1);
  };

  const handleRoleFilter = (nextRole) => {
    setRoleFilter(nextRole);
    setPage(1);
  };

  const rides = useMemo(() => {
    const instances = data?.data?.instances;
    if (!instances) return [];
    return [
      ...(instances.busing_subscription || []),
      ...(instances.bus_charter || []),
    ];
  }, [data]);

  const pagination = data?.data?.pagination || {
    page,
    limit,
    total: 0,
    totalPages: 0,
  };

  const handlePageChange = (nextPage) => setPage(nextPage);
  const handleLimitChange = (nextLimit) => {
    setLimit(nextLimit);
    setPage(1);
  };

  const handleViewDetails = (item) => {
    setSelectedRide(item);
    setShowDetail(true);
  };
  const handleCloseDetail = () => setShowDetail(false);

  const instance = selectedRide?.instance;
  const reservation = selectedRide?.reservation;
  const school = selectedRide?.school;
  const vendor = selectedRide?.vendor;
  const vehicle = selectedRide?.vehicle;
  const route = selectedRide?.route;
  const driver = selectedRide?.driver;
  const students = selectedRide?.students || [];
  const stats = selectedRide?.attendance_stats;

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between">
        <div className="mb-2 total">
          {t("total")} {t("cancelled")} {t("reservations")}:{" "}
          {pagination?.total ?? 0}
        </div>
        <div className="mb-2 d-flex flex-wrap gap-2">
          {role === "super_admin" && (
            <div style={{ minWidth: 220 }}>
              <select
                className="form-select"
                value={roleFilter || ""}
                onChange={(e) => handleRoleFilter(e.target.value || null)}
              >
                <option value="">
                  {t("all")} {t("reservations")}
                </option>
                <option value="school">
                  {t("schools")} {t("reservation")}
                </option>
                <option value="vendor">
                  {t("vendors")} {t("reservation")}
                </option>
                <option value="transfer_to_vendor">
                  {t("transfer_to")} {t("vendors")}
                </option>
              </select>
            </div>
          )}
          <div style={{ minWidth: 220 }}>
            <select
              className="form-select"
              value={bookingTypeFilter || ""}
              onChange={(e) =>
                handleBookingTypeFilter(e.target.value || null)
              }
            >
              <option value="">
                {t("all")} {t("types")}
              </option>
              <option value="busing_subscription">
                {t("busing")} {t("subscription")}
              </option>
              <option value="bus_charter">
                {t("bus")} {t("charter")}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th className="text-nowrap">
                {t("Sr")}.{t("no")}.
              </th>
              <th className="text-nowrap">{t("date")}</th>
              <th className="text-nowrap">
                {t("booking")} {t("type")}
              </th>
              <th className="text-nowrap">{t("leg")}</th>
              <th className="text-nowrap">
                {t("driver")} {t("name")}
              </th>
              <th className="text-nowrap">
                {t("vehicle")} {t("license")} {t("plate")}
              </th>
              <th className="text-nowrap">{t("students")}</th>
              <th>{t("status")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={9} />
            ) : rides.length > 0 ? (
              rides.map((item, i) => (
                <tr key={item?.instance?.id ?? item?.reservation?.id ?? i}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item?.instance?.date)}
                  </td>
                  <td className="text-nowrap">
                    {formatText(item?.reservation?.booking_type)}
                  </td>
                  <td className="text-nowrap">
                    {TRIP_LEG_LABEL[item?.instance?.trip_leg] ||
                      formatText(item?.instance?.trip_leg)}
                  </td>
                  <td className="text-nowrap">
                    {item?.driver?.name || "Unassigned"}
                  </td>
                  <td className="text-nowrap">
                    {item?.vehicle?.license_number || "-"}
                  </td>
                  <td className="text-nowrap">
                    {item?.reservation?.booking_type === "bus_charter"
                      ? `${item?.reservation?.reserved_seats ?? 0} ${t("seats")}`
                      : (item?.attendance_stats?.total ??
                        item?.students?.length ??
                        0)}
                  </td>
                  <td>
                    <span className={getStatusBadge(item?.instance?.status)}>
                      {getStatusText(item?.instance?.status)}
                    </span>
                  </td>
                  <td>
                    <div className="actionBtn d-flex">
                      <ActionMenu
                        actions={[
                          {
                            label: `${t("reservation")} ${t("details")}`,
                            icon: <BsCalendarCheck />,
                            onClick: () => handleViewDetails(item),
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center">
                  {t("no")} {t("cancelled")} {t("reservations")} {t("found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Cancelled Reservation Details Modal */}
      <Modal
        show={showDetail}
        onHide={handleCloseDetail}
        size="lg"
        centered
        backdrop="static"
        className="detailModel"
      >
        <Modal.Header closeButton className="border-0 position-relative">
          <Modal.Title className="w-100 text-center">
            <h4 className="mb-0 fw-bold text-white">
              <i className="fas fa-ban me-2"></i>
              {t("cancelled")} {t("reservation")} {t("details")}
            </h4>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="pt-0"
          style={{ maxHeight: "80vh", overflowY: "auto" }}
        >
          {selectedRide && (
            <div className="reservation-details-container">
              {/* Header Card with Status */}
              <div className="header-card mb-4 mt-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="reservation-id">
                    <span className="badge bg-light text-dark fs-6">
                      <i className="fas fa-hashtag me-2"></i>
                      {t("ID")}: <strong>{instance?.id || "N/A"}</strong>
                    </span>
                  </div>
                  <span className={getStatusBadge(instance?.status)}>
                    {getStatusText(instance?.status)}
                  </span>
                </div>
              </div>

              <div className="info-grid mb-4">
                <div className="row">
                  {/* Left Column */}
                  <div className="col-md-6">
                    {school && (
                      <div className="info-card mb-3">
                        <div className="info-header">
                          <i className="fas fa-school me-2 text-primary"></i>
                          <h6 className="mb-0 fw-bold">
                            {t("school")} {t("information")}
                          </h6>
                        </div>
                        <div className="info-body">
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-tag me-2"></i>
                              {t("full")} {t("name")}:
                            </span>
                            <span className="info-value">
                              {school?.name || "N/A"}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-envelope me-2"></i>
                              {t("email")}:
                            </span>
                            <span className="info-value">
                              {school?.email || "N/A"}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-bus me-2"></i>
                              {t("company")} {t("name")}:
                            </span>
                            <span className="info-value">
                              {school?.company_name || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-calendar-alt me-2 text-primary"></i>
                        <h6 className="mb-0 fw-bold">
                          {t("booking")} {t("information")}
                        </h6>
                      </div>
                      <div className="info-body">
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-tag me-2"></i>
                            {t("booking")} {t("type")}:
                          </span>
                          <span className="info-value">
                            {formatText(reservation?.booking_type)}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-road me-2"></i>
                            {t("leg")}:
                          </span>
                          <span className="info-value">
                            {TRIP_LEG_LABEL[instance?.trip_leg] ||
                              formatText(instance?.trip_leg)}
                          </span>
                        </div>
                        {reservation?.subscription_type && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-bus me-2"></i>
                              {t("subscription")} {t("type")}:
                            </span>
                            <span className="info-value">
                              {formatText(reservation.subscription_type)}
                            </span>
                          </div>
                        )}
                        {reservation?.charter_type && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-road me-2"></i>
                              {t("charter")} {t("type")}:
                            </span>
                            <span className="info-value">
                              {formatText(reservation.charter_type)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-bus me-2 text-primary"></i>
                        <h6 className="mb-0 fw-bold">
                          {t("vehicle")} {t("details")}
                        </h6>
                      </div>
                      <div className="info-body">
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-car me-2"></i>
                            {t("vehicle")} {t("type")}:
                          </span>
                          <span className="info-value">
                            {vehicle?.type || "N/A"}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-id-card me-2"></i>
                            {t("license")} {t("plate")}:
                          </span>
                          <span className="info-value">
                            {vehicle?.license_number || "N/A"}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-users me-2"></i>
                            {t("seats")}:
                          </span>
                          <span className="info-value">
                            {vehicle?.seats || "N/A"}
                          </span>
                        </div>
                        {reservation?.booking_type === "bus_charter" && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-user-friends me-2"></i>
                              {t("reserved")} {t("seats")}:
                            </span>
                            <span className="info-value">
                              {reservation?.reserved_seats ?? 0}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="col-md-6">
                    {vendor && (
                      <div className="info-card mb-3">
                        <div className="info-header">
                          <i className="fas fa-user-tie me-2 text-primary"></i>
                          <h6 className="mb-0 fw-bold">
                            {t("vendor")} {t("information")}
                          </h6>
                        </div>
                        <div className="info-body">
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-user me-2"></i>
                              {t("full")} {t("name")}:
                            </span>
                            <span className="info-value">
                              {vendor?.name || "N/A"}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-user me-2"></i>
                              {t("email")}:
                            </span>
                            <span className="info-value">
                              {vendor?.email || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-id-badge me-2 text-primary"></i>
                        <h6 className="mb-0 fw-bold">{t("driver")}</h6>
                      </div>
                      <div className="info-body">
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-user me-2"></i>
                            {t("full")} {t("name")}:
                          </span>
                          <span
                            className={`info-value ${
                              !driver?.name ? "text-warning" : ""
                            }`}
                          >
                            {driver?.name || t("unassigned")}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-envelope me-2"></i>
                            {t("email")}:
                          </span>
                          <span className="info-value">
                            {driver?.email || "N/A"}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-phone me-2"></i>
                            {t("phone")}:
                          </span>
                          <span className="info-value">
                            {driver?.phone || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-ban me-2 text-danger"></i>
                        <h6 className="mb-0 fw-bold">
                          {t("cancellation")} {t("details")}
                        </h6>
                      </div>
                      <div className="info-body">
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-calendar-day me-2"></i>
                            {t("date")}:
                          </span>
                          <span className="info-value">
                            {formatDateToLocal(instance?.date)}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-clock me-2"></i>
                            {t("start")} {t("time")}:
                          </span>
                          <span className="info-value">
                            {instance?.start_time
                              ? convertTime(instance.start_time)
                              : "N/A"}
                          </span>
                        </div>
                        {instance?.cancelled_at && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-ban me-2"></i>
                              {t("cancelled")} {t("at")}:
                            </span>
                            <span className="info-value">
                              {formatDateTime(instance.cancelled_at)}
                            </span>
                          </div>
                        )}
                        {instance?.cancelled_by && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-user-times me-2"></i>
                              {t("cancelled")} {t("by")}:
                            </span>
                            <span className="info-value">
                              {t("user")} #{instance.cancelled_by}
                            </span>
                          </div>
                        )}
                        {instance?.cancellation_reason && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-comment-alt me-2"></i>
                              {t("reason")}:
                            </span>
                            <span className="info-value">
                              {instance.cancellation_reason}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="location-section mb-4">
                <div className="row">
                  <div className="col-md-6">
                    <div className="location-card pickup">
                      <div className="location-header bg-success text-white">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        {t("pickup")} {t("location")}
                      </div>
                      <div className="location-body">
                        <p className="mb-0">
                          <i className="fas fa-location-dot text-danger me-2"></i>
                          {route?.pickup_address ||
                            reservation?.pickup_address ||
                            "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="location-card destination">
                      <div className="location-header bg-success text-white">
                        <i className="fas fa-flag-checkered me-2"></i>
                        {t("destination")}
                      </div>
                      <div className="location-body">
                        <p className="mb-0">
                          <i className="fas fa-map-pin text-success me-2"></i>
                          {route?.dropoff_address ||
                            reservation?.destination_address ||
                            "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Attendance Stats — not applicable to bus_charter */}
              {stats && reservation?.booking_type !== "bus_charter" && (
                <div className="info-card mb-4">
                  <div className="info-header">
                    <i className="fas fa-chart-pie me-2 text-primary"></i>
                    <h6 className="mb-0 fw-bold">Attendance Stats</h6>
                  </div>
                  <div className="info-body">
                    <div className="row text-center g-2">
                      <div className="col-4 col-md-2">
                        <div className="fw-bold">{stats.total}</div>
                        <div className="text-muted small">{t("total")}</div>
                      </div>
                      <div className="col-4 col-md-2">
                        <div className="fw-bold text-success">
                          {stats.dropped_off}
                        </div>
                        <div className="text-muted small">Dropped Off</div>
                      </div>
                      <div className="col-4 col-md-2">
                        <div className="fw-bold text-primary">
                          {stats.picked_up}
                        </div>
                        <div className="text-muted small">Picked Up</div>
                      </div>
                      <div className="col-4 col-md-2">
                        <div className="fw-bold text-warning">
                          {stats.scheduled}
                        </div>
                        <div className="text-muted small">
                          {t("scheduled")}
                        </div>
                      </div>
                      <div className="col-4 col-md-2">
                        <div className="fw-bold text-secondary">
                          {stats.absent}
                        </div>
                        <div className="text-muted small">Absent</div>
                      </div>
                      <div className="col-4 col-md-2">
                        <div className="fw-bold text-danger">
                          {stats.cancelled}
                        </div>
                        <div className="text-muted small">{t("cancel")}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Students — not applicable to bus_charter, which tracks
                  reserved seats rather than individually assigned students */}
              {reservation?.booking_type !== "bus_charter" && (
                <div className="info-card mb-3">
                  <div className="info-header">
                    <i className="fas fa-users me-2 text-primary"></i>
                    <h6 className="mb-0 fw-bold">
                      {t("students")} ({students.length})
                    </h6>
                  </div>
                  <div className="info-body p-0">
                    {students.length > 0 ? (
                      <div className="students-table-wrap">
                        <table className="students-table">
                          <thead>
                            <tr>
                              <th style={{ width: 40 }}>#</th>
                              <th>{t("name")}</th>
                              <th>
                                {t("roll")} {t("no")}.
                              </th>
                              <th>{t("parent")}</th>
                              <th style={{ width: 100 }}>{t("status")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {students.map((s, idx) => (
                              <tr key={s?.student?.id ?? idx}>
                                <td
                                  className="text-center text-muted"
                                  style={{ fontSize: 13 }}
                                >
                                  {idx + 1}
                                </td>
                                <td>
                                  <div className="student-name-cell">
                                    <div className="student-avatar">
                                      {(s?.student?.name ||
                                        "?")[0].toUpperCase()}
                                    </div>
                                    <span className="fw-semibold">
                                      {s?.student?.name || "N/A"}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className="roll-badge">
                                    {s?.student?.roll_number || "-"}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <i
                                      className="fas fa-user text-muted"
                                      style={{ fontSize: 11 }}
                                    ></i>
                                    <span style={{ fontSize: 13 }}>
                                      {s?.parent?.name || "N/A"}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className={getStatusBadge(
                                      s?.attendance?.status,
                                    )}
                                  >
                                    {getStatusText(s?.attendance?.status)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div
                        className="text-center text-muted py-4"
                        style={{ fontSize: 14 }}
                      >
                        <i
                          className="fas fa-user-slash mb-2 d-block"
                          style={{ fontSize: 22, color: "#ccc" }}
                        ></i>
                        {t("no")} {t("students")} {t("found")}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="footer-section">
                <div className="footer-info">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="meta-info">
                        <span className="meta-item">
                          <i className="far fa-calendar-plus me-2 text-muted"></i>
                          {t("created")}:{" "}
                          {instance?.created_at
                            ? formatDateToLocal(instance.created_at)
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6 text-end">
                      <div className="meta-info">
                        {instance?.updated_at && (
                          <span className="meta-item">
                            <i className="far fa-calendar-check me-2 text-muted"></i>
                            {t("updated")}:{" "}
                            {formatDateToLocal(instance.updated_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CancelledReservationsList;
